/**
 * API Client Service
 *
 * @module ApiClient
 * @description Centralized HTTP client for all backend API communication.
 *
 * ## Features:
 * - Axios-based HTTP client with interceptors
 * - Automatic JWT token handling (user, admin, referrer)
 * - Request deduplication for concurrent calls
 * - Error handling with 401 redirect
 *
 * ## Token Types:
 * - **auth_token**: Regular user authentication
 * - **admin_token**: Admin panel access
 * - **referrer_token**: Partner portal access
 *
 * ## API Endpoints:
 * - **Public**: /vaults, /config, /prices
 * - **Auth Required**: /investments/*, /claims/*
 * - **Admin Only**: /admin/*
 * - **Partner Only**: /referrer/*
 *
 * ## Usage:
 * ```typescript
 * import { api } from './api'
 *
 * // Public
 * const vaults = await api.getVaults()
 *
 * // Auth required (token added automatically)
 * const investments = await api.getMyInvestments()
 *
 * // Admin (uses admin_token)
 * const stats = await api.admin.getDashboardStats()
 * ```
 *
 * @see ConfigContext.tsx - Configuration provider
 * @see useAuth.ts - Authentication hook
 */

import axios, { AxiosInstance, AxiosError } from 'axios'
import { captureApiError, setUser as setSentryUser, addBreadcrumb } from '../config/sentry'
import { API_CONFIG } from '../config/app.config'
import type {
  ApiResponse,
  Tier,
  Vault,
  Investment,
  InvestmentCalculation,
  MarketplaceListing,
  User,
  LoginResponse,
  NonceResponse,
  CalculateInvestmentInput,
  CreateInvestmentInput,
} from '../types'

const API_URL = API_CONFIG.baseUrl

class ApiClient {
  private client: AxiosInstance
  private pendingConnectSolana: Map<string, Promise<any>> = new Map()

  constructor() {
    this.client = axios.create({
      baseURL: API_URL,
      timeout: API_CONFIG.timeout,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    // Request interceptor to add auth token
    this.client.interceptors.request.use((config) => {
      // Use admin_token for admin endpoints, referrer_token for /referrer/ endpoints, otherwise use auth_token
      const isAdminRequest = config.url?.startsWith('/admin/') || config.url?.startsWith('/partners/admin')
      const isReferrerRequest = config.url?.startsWith('/referrer/')
      
      let token: string | null = null
      if (isAdminRequest) {
        token = localStorage.getItem('admin_token')
      } else if (isReferrerRequest) {
        token = localStorage.getItem('referrer_token')
      } else {
        token = localStorage.getItem('auth_token')
      }
      
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }
      return config
    })

    // Response interceptor for error handling and Sentry reporting
    this.client.interceptors.response.use(
      (response) => {
        // Add breadcrumb for successful requests
        addBreadcrumb(
          `API ${response.config.method?.toUpperCase()} ${response.config.url}`,
          'api',
          { status: response.status }
        )
        return response
      },
      (error: AxiosError<ApiResponse>) => {
        const url = error.config?.url || ''
        const method = error.config?.method?.toUpperCase() || 'UNKNOWN'
        const statusCode = error.response?.status

        // Capture API errors to Sentry (4xx and 5xx)
        if (statusCode && statusCode >= 400) {
          // Don't capture 401 as errors (normal auth flow)
          if (statusCode !== 401) {
            captureApiError(
              error,
              url,
              method,
              statusCode,
              error.response?.data
            )
          }
        }

        if (statusCode === 401) {
          // Don't redirect for non-critical requests
          const skipRedirect = url.includes('/auth/') ||
                               url.includes('/vaults') ||
                               url.includes('/prices') ||
                               url.includes('/marketplace') ||
                               url.includes('/partners') ||
                               url.includes('/investments')

          if (!skipRedirect) {
            // Clear token and redirect to home only for protected endpoints
            localStorage.removeItem('auth_token')
            setSentryUser(null) // Clear Sentry user on logout
            window.location.href = '/'
          }
        }
        return Promise.reject(error)
      }
    )
  }

  // ==================== AUTH ====================

  async getNonce(walletAddress: string): Promise<NonceResponse> {
    const { data } = await this.client.get<NonceResponse>('/auth/nonce', {
      params: { walletAddress },
    })
    return data
  }

  async login(walletAddress: string, signature: string): Promise<LoginResponse> {
    const { data } = await this.client.post<LoginResponse>('/auth/login', {
      walletAddress,
      signature,
    })

    if (data.token) {
      localStorage.setItem('auth_token', data.token)

      // Set Sentry user context after successful login
      if (data.user) {
        setSentryUser({
          id: data.user.id,
          walletAddress: data.user.walletAddress,
          username: data.user.username,
          email: data.user.email,
        })
      }

      // Emit event to notify UserContext
      window.dispatchEvent(new Event('user-logged-in'))
    }

    // v3.0: Store partner info for unified login
    if (data.isPartner && data.partnerData) {
      localStorage.setItem('is_partner', 'true')
      localStorage.setItem('partner_data', JSON.stringify(data.partnerData))
    } else {
      localStorage.removeItem('is_partner')
      localStorage.removeItem('partner_data')
    }

    return data
  }

  async register(username: string, password: string, email: string, referralCode?: string): Promise<ApiResponse<{ email: string; expiresAt: Date }>> {
    const { data } = await this.client.post<ApiResponse<{ email: string; expiresAt: Date }>>('/auth/register', {
      username,
      password,
      email,
      referralCode,
    })

    // No token returned - user must verify email first
    return data
  }

  async verifyEmail(email: string, code: string): Promise<LoginResponse> {
    const { data } = await this.client.post<LoginResponse>('/auth/verify-email', {
      email,
      code,
    })

    if (data.token) {
      localStorage.setItem('auth_token', data.token)

      // Set Sentry user context after successful verification
      if (data.user) {
        setSentryUser({
          id: data.user.id,
          walletAddress: data.user.walletAddress,
          username: data.user.username,
          email: data.user.email,
        })
      }

      // Emit event to notify UserContext
      window.dispatchEvent(new Event('user-logged-in'))
    }

    return data
  }

  async resendOTP(email: string, type: 'registration' | 'password-reset' = 'registration'): Promise<ApiResponse<{ expiresAt: Date }>> {
    const { data } = await this.client.post<ApiResponse<{ expiresAt: Date }>>('/auth/resend-otp', {
      email,
      type,
    })

    return data
  }

  async forgotPassword(email: string): Promise<ApiResponse<{ expiresAt: Date }>> {
    const { data } = await this.client.post<ApiResponse<{ expiresAt: Date }>>('/auth/forgot-password', {
      email,
    })

    return data
  }

  async resetPassword(email: string, code: string, newPassword: string): Promise<ApiResponse<void>> {
    const { data } = await this.client.post<ApiResponse<void>>('/auth/reset-password', {
      email,
      code,
      newPassword,
    })

    return data
  }

  async requestEmailChange(newEmail: string): Promise<ApiResponse<void>> {
    const { data } = await this.client.post<ApiResponse<void>>('/auth/request-email-change', {
      newEmail,
    })

    return data
  }

  async verifyEmailChange(newEmail: string, code: string): Promise<ApiResponse<{ email: string }>> {
    const { data } = await this.client.post<ApiResponse<{ email: string }>>('/auth/verify-email-change', {
      newEmail,
      code,
    })

    return data
  }

  async loginWithPassword(username: string, password: string): Promise<LoginResponse> {
    const { data } = await this.client.post<LoginResponse>('/auth/login-password', {
      username,
      password,
    })

    if (data.token) {
      localStorage.setItem('auth_token', data.token)

      // Emit event to notify UserContext
      window.dispatchEvent(new Event('user-logged-in'))
    }

    return data
  }

  async getCurrentUser(): Promise<ApiResponse<User>> {
    const { data } = await this.client.get<ApiResponse<User>>('/auth/me')
    return data
  }

  async getUserReferralInfo(): Promise<ApiResponse<any>> {
    const { data } = await this.client.get<ApiResponse<any>>('/auth/referral-info')
    return data
  }

  async getUserReferralStats(): Promise<ApiResponse<any>> {
    const { data } = await this.client.get<ApiResponse<any>>('/auth/referral-stats')
    return data
  }

  async getReferralBonuses(): Promise<ApiResponse<any>> {
    const { data } = await this.client.get<ApiResponse<any>>('/investments/referral-bonuses')
    return data
  }

  async validateReferralCode(code: string): Promise<ApiResponse<{ valid: boolean; type?: string; name?: string; username?: string; message?: string }>> {
    const { data } = await this.client.get<ApiResponse<any>>('/auth/validate-referral', {
      params: { code }
    })
    return data
  }

  async applyReferralCode(referralCode: string): Promise<ApiResponse<any>> {
    const { data} = await this.client.post<ApiResponse<any>>('/auth/apply-referral', { referralCode })
    return data
  }

  async setReferralWalletAddress(walletAddress: string): Promise<ApiResponse<{ referralWalletAddress: string }>> {
    const { data } = await this.client.post<ApiResponse<{ referralWalletAddress: string }>>('/auth/referral-wallet', { walletAddress })
    return data
  }

  logout(): void {
    localStorage.removeItem('auth_token')
    // v3.0: Clear partner info on logout
    localStorage.removeItem('is_partner')
    localStorage.removeItem('partner_data')

    // Emit event to notify UserContext
    window.dispatchEvent(new Event('user-logged-out'))
  }

  isAuthenticated(): boolean {
    return !!localStorage.getItem('auth_token')
  }

  // v3.0: Check if current user is a linked partner
  isPartner(): boolean {
    return localStorage.getItem('is_partner') === 'true'
  }

  // v3.0: Get partner data from localStorage
  getPartnerData(): { id: string; name: string; referralCode: string; isActive: boolean } | null {
    const data = localStorage.getItem('partner_data')
    if (!data) return null
    try {
      return JSON.parse(data)
    } catch {
      return null
    }
  }

  async connectSolana(walletAddress: string): Promise<ApiResponse> {
    // Deduplicate concurrent requests for the same wallet address
    const existingRequest = this.pendingConnectSolana.get(walletAddress)
    if (existingRequest) {
      return existingRequest
    }

    const request = this.client.post<ApiResponse>('/auth/connect-solana', {
      walletAddress,
    }).then((response) => {
      this.pendingConnectSolana.delete(walletAddress)
      return response.data
    }).catch((error) => {
      this.pendingConnectSolana.delete(walletAddress)
      throw error
    })

    this.pendingConnectSolana.set(walletAddress, request)
    return request
  }

  // ==================== TIERS (v4.0) ====================

  async getTiers(): Promise<ApiResponse<Tier[]>> {
    const { data } = await this.client.get<ApiResponse<Tier[]>>('/tiers')
    return data
  }

  async getTierById(id: string): Promise<ApiResponse<Tier>> {
    const { data } = await this.client.get<ApiResponse<Tier>>(`/tiers/${id}`)
    return data
  }

  // ==================== VAULTS ====================

  async getVaults(params?: {
    tierId?: string
    duration?: number
    isActive?: boolean
  }): Promise<ApiResponse<Vault[]>> {
    const { data } = await this.client.get<ApiResponse<Vault[]>>('/vaults', { params })
    return data
  }

  async getVaultById(id: string): Promise<ApiResponse<any>> {
    const { data } = await this.client.get<ApiResponse<any>>(`/vaults/${id}`)
    return data
  }

  async calculateInvestment(
    vaultId: string,
    input: CalculateInvestmentInput
  ): Promise<ApiResponse<InvestmentCalculation>> {
    const { data } = await this.client.post<ApiResponse<InvestmentCalculation>>(
      `/vaults/${vaultId}/calculate`,
      input
    )
    return data
  }

  // ==================== INVESTMENTS ====================

  async createInvestment(input: CreateInvestmentInput): Promise<ApiResponse> {
    const { data} = await this.client.post<ApiResponse>('/investments', input)
    return data
  }

  async getMyInvestments(status?: string, includeNFTOwned: boolean = true): Promise<ApiResponse<Investment[]>> {
    const { data } = await this.client.get<ApiResponse<Investment[]>>('/investments/my', {
      params: { status, includeNFTOwned: includeNFTOwned.toString() },
    })
    return data
  }

  async getInvestmentById(id: string): Promise<ApiResponse<any>> {
    const { data } = await this.client.get<ApiResponse<any>>(`/investments/${id}`)
    return data
  }

  async claimYield(investmentId: string): Promise<ApiResponse> {
    const { data } = await this.client.post<ApiResponse>(
      `/investments/${investmentId}/claim-yield`
    )
    return data
  }

  async claimTakara(investmentId: string): Promise<ApiResponse> {
    const { data } = await this.client.post<ApiResponse>(
      `/investments/${investmentId}/claim-takara`
    )
    return data
  }

  async confirmClaim(claimLogId: string, txSignature: string): Promise<ApiResponse> {
    const { data } = await this.client.post<ApiResponse>(`/claims/${claimLogId}/confirm`, { txSignature })
    return data
  }

  async claimReferralUsdt(): Promise<ApiResponse> {
    const { data } = await this.client.post<ApiResponse>('/referrals/claim-usdt')
    return data
  }

  async claimReferralTakara(): Promise<ApiResponse> {
    const { data } = await this.client.post<ApiResponse>('/referrals/claim-takara')
    return data
  }

  // ==================== NFT MINTING (v3.3) ====================

  async prepareNFTMint(investmentId: string): Promise<ApiResponse<{
    transaction: string
    metadataUri: string
    mintAddress: string
    message: string
  }>> {
    const { data } = await this.client.get<ApiResponse<any>>(
      `/investments/${investmentId}/nft/prepare-mint`
    )
    return data
  }

  async submitSignedNFTMint(investmentId: string, signedTransaction: string): Promise<ApiResponse<{
    mintAddress: string
    txSignature: string
  }>> {
    const { data } = await this.client.post<ApiResponse<any>>(
      `/investments/${investmentId}/nft/submit-signed`,
      { signedTransaction }
    )
    return data
  }

  async confirmNFTMint(investmentId: string, txSignature: string, mintAddress: string): Promise<ApiResponse> {
    const { data } = await this.client.post<ApiResponse>(
      `/investments/${investmentId}/nft/confirm-mint`,
      { txSignature, mintAddress }
    )
    return data
  }

  async submitPendingMintConfirm(investmentId: string, txSignature: string, mintAddress: string): Promise<ApiResponse> {
    const { data } = await this.client.post<ApiResponse>(
      `/investments/${investmentId}/nft/pending-confirm`,
      { txSignature, mintAddress }
    )
    return data
  }

  // ==================== MARKETPLACE ====================

  async getMarketplaceListings(params?: {
    status?: string
    sortBy?: string
    sortOrder?: string
    minPrice?: number
    maxPrice?: number
  }): Promise<ApiResponse<MarketplaceListing[]>> {
    const { data } = await this.client.get<ApiResponse<MarketplaceListing[]>>(
      '/marketplace',
      { params }
    )
    return data
  }

  async getMarketplaceStats(): Promise<ApiResponse> {
    const { data } = await this.client.get<ApiResponse>('/marketplace/stats')
    return data
  }

  async listNFT(investmentId: string, priceUSDT: number): Promise<ApiResponse> {
    const { data } = await this.client.post<ApiResponse>('/marketplace/list', {
      investmentId,
      priceUSDT,
    })
    return data
  }

  async purchaseNFT(listingId: string, txSignature: string, paymentType: 'USDT' | 'TAKARA' = 'USDT'): Promise<ApiResponse> {
    const { data } = await this.client.post<ApiResponse>(`/marketplace/${listingId}/buy`, {
      txSignature,
      paymentType,
    })
    return data
  }

  async cancelListing(listingId: string): Promise<ApiResponse> {
    const { data } = await this.client.delete<ApiResponse>(`/marketplace/${listingId}`)
    return data
  }

  async getMyListings(): Promise<ApiResponse<any[]>> {
    const { data } = await this.client.get<ApiResponse<any[]>>('/marketplace/my-listings')
    return data
  }

  // ==================== TAKARA BOOST (v2.2) ====================

  async applyTakaraBoost(investmentId: string, takaraAmount: number, takaraPrice: number): Promise<ApiResponse> {
    const { data } = await this.client.post<ApiResponse>(`/investments/${investmentId}/boost/takara`, {
      takaraAmount,
      takaraPrice,
    })
    return data
  }

  async getTakaraBoost(investmentId: string): Promise<ApiResponse> {
    const { data } = await this.client.get<ApiResponse>(`/investments/${investmentId}/boost/takara`)
    return data
  }

  // ==================== PRICES (v2.7) ====================

  async getLaikaPrice(): Promise<ApiResponse<{ token: string; price: number; currency: string; timestamp: string }>> {
    const { data } = await this.client.get<ApiResponse<{ token: string; price: number; currency: string; timestamp: string }>>('/prices/laika')
    return data
  }

  async getTakaraPrice(): Promise<ApiResponse<{ token: string; price: number; currency: string; timestamp: string }>> {
    const { data } = await this.client.get<ApiResponse<{ token: string; price: number; currency: string; timestamp: string }>>('/prices/takara')
    return data
  }

  async getBtcPrice(): Promise<ApiResponse<{ token: string; price: number; currency: string; source: string; timestamp: string }>> {
    const { data } = await this.client.get<ApiResponse<{ token: string; price: number; currency: string; source: string; timestamp: string }>>('/prices/btc')
    return data
  }

  async getAllPrices(): Promise<ApiResponse<{
    laika: { token: string; price: number; currency: string };
    takara: { token: string; price: number; currency: string };
    timestamp: string;
  }>> {
    const { data } = await this.client.get<ApiResponse<any>>('/prices')
    return data
  }

  // ==================== TOKENOMICS (v3.0 - TOKARA Model) ====================

  async getTokenomics(): Promise<ApiResponse<{
    tvl: { current: number; target: number; progress: number };
    pricing: { internal: number; listing: number; discount: number };
    apy: { display: number; base: number };
    modifiers: { ldf: number; boost: number };
    dexLiquidity: number;
    calculatedAt: string;
  }>> {
    const { data } = await this.client.get<ApiResponse<any>>('/prices/tokenomics')
    return data
  }

  async getTVLBreakdown(): Promise<ApiResponse<{
    totalTVL: number;
    activeInvestments: number;
    vaultBreakdown: Array<{
      vaultId: string;
      vaultName: string;
      tier: string;
      tvl: number;
      count: number;
      percentOfTotal: number;
    }>;
    growth: {
      currentTVL: number;
      growth24h: number;
      growth7d: number;
      growth30d: number;
    };
    milestones: {
      current: number;
      milestones: Array<{ amount: number; label: string; reached: boolean; percentProgress: number }>;
      nextMilestone: { amount: number; label: string; remaining: number } | null;
    };
    calculatedAt: string;
  }>> {
    const { data } = await this.client.get<ApiResponse<any>>('/prices/tvl')
    return data
  }

  async getAPYTable(): Promise<ApiResponse<{
    currentTVL: number;
    table: Array<{
      tvl: number;
      tvlFormatted: string;
      ldf: number;
      internalPrice: number;
      discount: number;
      boost: number;
      displayAPY: number;
      isCurrent: boolean;
    }>;
    formula: {
      ldf: string;
      price: string;
      boost: string;
      apy: string;
    };
  }>> {
    const { data } = await this.client.get<ApiResponse<any>>('/prices/apy-table')
    return data
  }

  async calculateTokenomics(tvl?: number): Promise<ApiResponse<any>> {
    const params = tvl !== undefined ? { tvl } : {}
    const { data } = await this.client.get<ApiResponse<any>>('/prices/calculator', { params })
    return data
  }

  async getSupplyBreakdown(): Promise<ApiResponse<{
    circulatingSupply: number;
    totalSupply: number;
    percentCirculating: number;
    remaining: number;
    bitcoinVault: {
      threshold: number;
      progress: number;
      unlocked: boolean;
    };
    breakdown: {
      mined: number;
      entryLocked: number;
      boostLocked: number;
      treasury: number;
    };
    calculatedAt: string;
  }>> {
    const { data } = await this.client.get<ApiResponse<any>>('/prices/supply')
    return data
  }

  // ==================== INSTANT SALE (v2.2) ====================

  async toggleInstantSale(investmentId: string, enabled: boolean): Promise<ApiResponse> {
    const { data } = await this.client.put<ApiResponse>(`/investments/${investmentId}/instant-sale`, {
      enabled,
    })
    return data
  }

  async executeInstantSale(investmentId: string): Promise<ApiResponse> {
    const { data} = await this.client.post<ApiResponse>(`/investments/${investmentId}/instant-sale/execute`)
    return data
  }

  async getInstantSalePrice(investmentId: string): Promise<ApiResponse> {
    const { data } = await this.client.get<ApiResponse>(`/investments/${investmentId}/instant-sale/price`)
    return data
  }

  // ==================== PARTNERS (Public) ====================

  async getPartners(): Promise<ApiResponse<any[]>> {
    const { data } = await this.client.get<ApiResponse<any[]>>('/partners')
    return data
  }

  // ==================== ADMIN: PARTNERS ====================

  async adminGetPartners(): Promise<ApiResponse<any[]>> {
    const { data } = await this.client.get<ApiResponse<any[]>>('/partners/admin')
    return data
  }

  async adminCreatePartner(partner: {
    name: string
    logoUrl: string
    websiteUrl?: string
    order?: number
  }): Promise<ApiResponse> {
    const { data } = await this.client.post<ApiResponse>('/partners/admin', partner)
    return data
  }

  async adminUpdatePartner(id: string, partner: {
    name?: string
    logoUrl?: string
    websiteUrl?: string
    order?: number
    isActive?: boolean
  }): Promise<ApiResponse> {
    const { data } = await this.client.put<ApiResponse>(`/partners/admin/${id}`, partner)
    return data
  }

  async adminDeletePartner(id: string): Promise<ApiResponse> {
    const { data } = await this.client.delete<ApiResponse>(`/partners/admin/${id}`)
    return data
  }

  async adminUploadPartnerLogo(file: File): Promise<ApiResponse<{ url: string }>> {
    const formData = new FormData()
    formData.append('logo', file)
    // Use native fetch for file uploads to avoid axios Content-Type issues
    const token = localStorage.getItem('admin_token')
    const response = await fetch(`${API_URL}/partners/admin/upload`, {
      method: 'POST',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      body: formData,
      credentials: 'include',
    })
    const data = await response.json()
    if (!response.ok) {
      throw { response: { data } }
    }
    return data
  }

  // ==================== ADMIN REFERRERS (v2.8) ====================

  async adminGetReferrers(): Promise<ApiResponse<any>> {
    const { data } = await this.client.get<ApiResponse<any>>('/admin/referrers')
    return data
  }

  async adminCreateReferrer(referrer: {
    name: string
    email: string
    password: string
    walletAddress?: string
  }): Promise<ApiResponse<any>> {
    const { data } = await this.client.post<ApiResponse<any>>('/admin/referrers', referrer)
    return data
  }

  async adminGetReferrer(id: string): Promise<ApiResponse<any>> {
    const { data } = await this.client.get<ApiResponse<any>>(`/admin/referrers/${id}`)
    return data
  }

  async adminUpdateReferrer(id: string, updates: {
    name?: string
    email?: string
    password?: string
    walletAddress?: string | null
    isActive?: boolean
  }): Promise<ApiResponse<any>> {
    const { data } = await this.client.put<ApiResponse<any>>(`/admin/referrers/${id}`, updates)
    return data
  }

  async adminGetReferrerTree(id: string): Promise<ApiResponse<any>> {
    const { data } = await this.client.get<ApiResponse<any>>(`/admin/referrers/${id}/tree`)
    return data
  }

  async adminGetReferrerRewards(id: string): Promise<ApiResponse<any>> {
    const { data } = await this.client.get<ApiResponse<any>>(`/admin/referrers/${id}/rewards`)
    return data
  }

  async adminProcessReferrerPayout(id: string, rewardIds: string[], txSignature?: string): Promise<ApiResponse<any>> {
    const { data } = await this.client.post<ApiResponse<any>>(`/admin/referrers/${id}/payout`, { rewardIds, txSignature })
    return data
  }

  async adminDeactivateReferrer(id: string): Promise<ApiResponse<any>> {
    const { data } = await this.client.delete<ApiResponse<any>>(`/admin/referrers/${id}`)
    return data
  }

  async adminGetReferralStats(): Promise<ApiResponse<any>> {
    const { data } = await this.client.get<ApiResponse<any>>('/admin/referrers/stats')
    return data
  }

  // ==================== REFERRER PORTAL (v2.8) ====================

  async referrerLogin(email: string, password: string): Promise<ApiResponse<any>> {
    const { data } = await this.client.post<ApiResponse<any>>('/referrer/login', { email, password })
    return data
  }

  async referrerLogout(): Promise<ApiResponse<any>> {
    const { data } = await this.client.post<ApiResponse<any>>('/referrer/logout')
    return data
  }

  async referrerGetProfile(): Promise<ApiResponse<any>> {
    const { data } = await this.client.get<ApiResponse<any>>('/referrer/me')
    return data
  }

  async referrerGetReferrals(): Promise<ApiResponse<any>> {
    const { data } = await this.client.get<ApiResponse<any>>('/referrer/referrals')
    return data
  }

  async referrerGetRewards(): Promise<ApiResponse<any>> {
    const { data } = await this.client.get<ApiResponse<any>>('/referrer/rewards')
    return data
  }

  async referrerGetPending(): Promise<ApiResponse<any>> {
    const { data } = await this.client.get<ApiResponse<any>>('/referrer/pending')
    return data
  }

  async referrerGetTree(): Promise<ApiResponse<any>> {
    const { data } = await this.client.get<ApiResponse<any>>('/referrer/tree')
    return data
  }

  async referrerLinkWallet(email: string, password: string, walletAddress: string): Promise<ApiResponse<any>> {
    const { data } = await this.client.post<ApiResponse<any>>('/referrer/link-wallet', {
      email,
      password,
      walletAddress
    })
    return data
  }
}

// Export singleton instance
export const api = new ApiClient()
export default api